import {
  PayMethodEventDetailItem,
  PayMethodExhibitItem,
  PayMethodExhibitResponseItem,
} from '../../../lib/api/payMethodExhibit';
import {
  PgProviderRouteItem,
  PgProviderRoutesMaster,
  PgProviderRouteResponseItem,
} from '../../../lib/api/pgProviderRoute';
import { getAccountTypeLabel } from '../constants/labels';
import { PRODUCT_TYPE_LABEL } from '../constants/payMethodEventDetail';
import { EditableEventDetail, EditablePayMethod, EditableRoute, SettingChange } from '../types';

export const moveItem = <TItem>(list: TItem[], from: number, to: number): TItem[] => {
  if (from < 0 || to < 0 || from >= list.length || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

/**
 * 서버 응답에 편집용 `rowKey` 를 붙인다.
 *
 * 응답이 같으면 같은 키가 나오도록 **결정적으로** 만든다 — 렌더마다 새 키를 뽑으면
 * 초안 비교(JSON)가 매번 달라져 미저장 표시가 멋대로 켜진다. 편집 중 추가되는 줄만 임의 키를 쓴다.
 */
export const toEditablePayMethods = (list: PayMethodExhibitResponseItem[]): EditablePayMethod[] =>
  list.map((method) => ({
    ...method,
    eventDetails: method.eventDetails.map((detail, index) => ({
      ...detail,
      rowKey: `saved-${method.id}-${detail.productType}-${index}`,
    })),
  }));

/** 안내문 한 줄에서 화면 전용 키를 걷어낸 형태. 전송과 비교가 같은 기준을 쓰게 한 곳에 둔다. */
const toEventDetailPayload = (detail: EditableEventDetail): PayMethodEventDetailItem => ({
  productType: detail.productType,
  content: detail.content,
  isActive: detail.isActive,
});

/**
 * 미저장 변경 비교용 형태 — 화면 전용 키만 뺀다.
 *
 * 🔴 `toPayMethodPayload` 를 그대로 쓰면 안 된다. 그쪽은 빈 문구를 걸러내므로, 빈 줄을
 * 새로 추가한 것이 "변경 없음" 으로 잡혀 저장 바가 안 뜬다.
 * 반대로 키를 비교에 넣으면, 마지막 줄을 지우고 같은 내용으로 다시 추가했을 때
 * 미저장 표시만 켜지고 변경 목록은 비어 사용자가 저장할 것도 되돌릴 것도 없는 상태가 된다.
 */
export const toComparablePayMethods = (list: EditablePayMethod[]): unknown =>
  list.map((method) => ({
    ...method,
    eventDetails: method.eventDetails.map(toEventDetailPayload),
  }));

export const toPayMethodPayload = (list: EditablePayMethod[]): PayMethodExhibitItem[] =>
  list.map((method, index) => ({
    id: method.id,
    isExhibit: method.isExhibit,
    // 화면 순서대로 1부터 다시 매긴다. 저장할 때마다 전량 교체라 사이를 띄울 이유가 없다.
    sortOrder: index + 1,
    eventName: method.eventName?.trim() || null,
    eventDetails: method.eventDetails
      .filter((detail) => detail.content.trim())
      .map((detail) => ({ ...toEventDetailPayload(detail), content: detail.content.trim() })),
  }));

export const getPayMethodChanges = (before: EditablePayMethod[], after: EditablePayMethod[]): SettingChange[] => {
  const changes: SettingChange[] = [];
  const detailsText = (method: PayMethodExhibitItem): string =>
    method.eventDetails
      .map(
        (detail) =>
          `${PRODUCT_TYPE_LABEL[detail.productType]} · ${detail.isActive ? '노출' : '숨김'}: ${detail.content}`,
      )
      .join('\n');
  const oldList = toPayMethodPayload(before);
  toPayMethodPayload(after).forEach((method) => {
    const old = oldList.find((item) => item.id === method.id);
    const name = after.find((item) => item.id === method.id)?.name ?? '';
    if (!old) return;
    if (old.isExhibit !== method.isExhibit)
      changes.push({
        label: `${name} 노출`,
        before: old.isExhibit ? '노출' : '숨김',
        after: method.isExhibit ? '노출' : '숨김',
      });
    if (old.eventName !== method.eventName)
      changes.push({ label: `${name} 배지`, before: old.eventName ?? '', after: method.eventName ?? '' });
    if (detailsText(old) !== detailsText(method))
      changes.push({ label: `${name} 안내문`, before: detailsText(old), after: detailsText(method) });
  });
  if (before.map((item) => item.id).join() !== after.map((item) => item.id).join())
    changes.push({
      label: '결제수단 순서',
      before: before.map((item) => item.name).join(' → '),
      after: after.map((item) => item.name).join(' → '),
    });
  return changes;
};

export const toEditableRoutes = (list: PgProviderRouteResponseItem[]): EditableRoute[] =>
  list.map((route) => ({ ...route, rowKey: `saved-${route.id}` }));

/** 저장과 미리보기 모두 같은 화면 순서를 사용한다. */
export const toRoutePayload = (list: EditableRoute[]): PgProviderRouteItem[] =>
  list.map((route, index) => ({
    payMethodId: route.payMethodId,
    pgProviderId: route.pgProviderId,
    accountType: route.accountType,
    maxAmount: route.maxAmount,
    priority: (index + 1) * 10,
    isActive: route.isActive,
    memo: route.memo,
  }));

export const describeRoute = (route: PgProviderRouteItem, master: PgProviderRoutesMaster): string => {
  const method = master.payMethods.find((item) => item.id === route.payMethodId)?.name ?? '결제수단 미선택';
  const pg = master.pgProviders.find((item) => item.id === route.pgProviderId)?.korName ?? 'PG 미선택';
  return `${method} · ${getAccountTypeLabel(route.accountType)} · ${route.maxAmount === null ? '금액 제한 없음' : `${route.maxAmount.toLocaleString('ko-KR')}원 이하`} → ${pg}${route.isActive ? '' : ' (미사용)'}`;
};

export const getRouteChanges = (
  before: EditableRoute[],
  after: EditableRoute[],
  master: PgProviderRoutesMaster,
): SettingChange[] => {
  const changes: SettingChange[] = [];
  before.forEach((old) => {
    if (!after.some((item) => item.rowKey === old.rowKey))
      changes.push({ label: '규칙 삭제', before: describeRoute(old, master), after: '삭제' });
  });
  after.forEach((item, index) => {
    const old = before.find((candidate) => candidate.rowKey === item.rowKey);
    if (!old || JSON.stringify(toRoutePayload([old])) !== JSON.stringify(toRoutePayload([item])))
      changes.push({
        label: `규칙 ${index + 1} ${old ? '변경' : '추가'}`,
        before: old ? `${describeRoute(old, master)}${old.memo ? `\n메모: ${old.memo}` : ''}` : '',
        after: `${describeRoute(item, master)}${item.memo ? `\n메모: ${item.memo}` : ''}`,
      });
  });
  const commonBefore = before.filter((item) => after.some((candidate) => candidate.rowKey === item.rowKey));
  const commonAfter = after.filter((item) => before.some((candidate) => candidate.rowKey === item.rowKey));
  if (commonBefore.map((item) => item.rowKey).join() !== commonAfter.map((item) => item.rowKey).join())
    changes.push({
      label: '규칙 평가 순서',
      before: before.map((item, index) => `${index + 1}. ${describeRoute(item, master)}`).join('\n'),
      after: after.map((item, index) => `${index + 1}. ${describeRoute(item, master)}`).join('\n'),
    });
  return changes;
};
